import React, { useCallback, useContext, useState, ReactElement, useMemo, useRef } from 'react';
import { ExaminerProvider } from './Examiner.context';
import { SignalRProvider } from './SignalR.context';
import { AlertType } from '../const/alerts';

export type EnvironmentContext = 'examiner' | 'administrator';

interface ILayoutContext {
  isLoader: boolean;
  alertOpen: boolean;
  alertType: AlertType;
  alertMessage: string | ReactElement | undefined;
  loaderText?: string;
  setAlert: React.Dispatch<React.SetStateAction<Alert>>;
  setIsLoader: React.Dispatch<React.SetStateAction<boolean>>;
  openAlert: (alert: AlertType, message?: string | ReactElement) => void;
  showLoader: (isLoading: boolean, message?: string) => void;
  isSidebarMinimized: boolean;
  setIsSidebarMinimized: (isMinimized: boolean) => void;
  isTooltipOpen: React.MutableRefObject<boolean>;
  highContrastMode: boolean;
  isAnyDropdownOpen: React.MutableRefObject<boolean>;
  stableMenuAnchor: React.MutableRefObject<HTMLDivElement | null>;
  updateStableMenuAnchor: (left: number, top: number) => void;
  isCommentPopoverOpen: React.MutableRefObject<boolean>;
}

export const LayoutContextDefaultValue: ILayoutContext = {
  isLoader: false,
  alertOpen: false,
  alertType: 'error',
  alertMessage: undefined,
  loaderText: undefined,
  setAlert: () => null,
  setIsLoader: () => null,
  openAlert: () => null,
  showLoader: () => null,
  setIsSidebarMinimized: () => null,
  isSidebarMinimized: false,
  isTooltipOpen: { current: false },
  highContrastMode: false,
  isAnyDropdownOpen: { current: false },
  stableMenuAnchor: { current: null },
  updateStableMenuAnchor: () => null,
  isCommentPopoverOpen: { current: false },
};

interface Alert {
  alertOpen: boolean;
  alertType: AlertType;
  message?: string | ReactElement;
}
const LayoutContext = React.createContext<ILayoutContext>(LayoutContextDefaultValue);

export default LayoutContext;

export const LayoutProvider: React.FC = ({ children }) => {
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [loaderText, setLoaderText] = useState<string>('Pobieranie danych');
  const [alert, setAlert] = useState<Alert>({
    alertOpen: false,
    alertType: 'error',
    message: undefined,
  });

  const openAlert = useCallback((alertType: AlertType, message: string | ReactElement | undefined = undefined) => {
    setAlert({
      alertOpen: true,
      alertType,
      message,
    });
  }, []);

  const [isSidebarMinimized, setIsSidebarMinimized] = useState<boolean>(false);
  const isTooltipOpen = useRef<boolean>(false);
  const isAnyDropdownOpen = useRef<boolean>(false);
  const stableMenuAnchor = useRef<HTMLDivElement>(null);
  const isCommentPopoverOpen = useRef<boolean>(false);
  const isGradeRulesModalOpen = useRef<boolean>(false);

  const showLoader = useCallback((isLoading: boolean, message?: string) => {
    message ? setLoaderText(message) : setLoaderText('Pobieranie danych');
    setIsLoader(isLoading);
  }, []);

  const highContrastMode = window.matchMedia('(forced-colors: active)').matches;

  const updateStableMenuAnchor = useCallback((left: number, top: number) => {
    if (!stableMenuAnchor.current) {
      return;
    }
    stableMenuAnchor.current.style.left = `${Math.round(left)}px`;
    stableMenuAnchor.current.style.top = `${Math.round(top)}px`;
  }, []);

  const value = useMemo(() => {
    return {
      isLoader,
      alertOpen: alert.alertOpen,
      alertType: alert.alertType,
      alertMessage: alert.message,
      loaderText,
      setAlert,
      setIsLoader,
      openAlert,
      showLoader,
      isSidebarMinimized,
      setIsSidebarMinimized,
      isTooltipOpen,
      highContrastMode,
      isAnyDropdownOpen,
      isGradeRulesModalOpen,
      stableMenuAnchor,
      updateStableMenuAnchor,
      isCommentPopoverOpen,
    };
  }, [
    alert.alertOpen,
    alert.alertType,
    alert.message,
    highContrastMode,
    isAnyDropdownOpen,
    isLoader,
    isSidebarMinimized,
    isTooltipOpen,
    loaderText,
    isCommentPopoverOpen,
    isGradeRulesModalOpen,
    openAlert,
    showLoader,
    updateStableMenuAnchor,
  ]);

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};

export const useLayoutContext = (): ILayoutContext => {
  const context = useContext(LayoutContext);
  if (!context) throw new Error('no <LayoutContext> wrapper');
  return context;
};

export const useShowLoader = (): typeof context.showLoader => {
  const context = useContext(LayoutContext);
  if (!context) throw new Error('no <LayoutContext> wrapper');
  return context.showLoader;
};

export const useOpenAlert = (): typeof context.openAlert => {
  const context = useContext(LayoutContext);
  if (!context) throw new Error('no <LayoutContext> wrapper');
  return context.openAlert;
};

export const useIsTooltipOpen = (): typeof context.isTooltipOpen => {
  const context = useContext(LayoutContext);
  if (!context) throw new Error('no <LayoutContext> wrapper');
  return context.isTooltipOpen;
};

export const useIsAnyDropdownOpen = (): typeof context.isAnyDropdownOpen => {
  const context = useContext(LayoutContext);
  if (!context) throw new Error('no <LayoutContext> wrapper');
  return context.isAnyDropdownOpen;
};

export const useCommentPopoverOpen = (): typeof context.isCommentPopoverOpen => {
  const context = useContext(LayoutContext);
  if (!context) throw new Error('no <LayoutContext> wrapper');
  return context.isCommentPopoverOpen;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function withLayoutContextrProvider<T>(WrappedComponent: any) {
  const AppContext = (props: T) => {
    return (
      <SignalRProvider>
        <LayoutProvider>
          <ExaminerProvider>
            <WrappedComponent {...props} />
          </ExaminerProvider>
        </LayoutProvider>
      </SignalRProvider>
    );
  };
  // AppContext.displayName = WrappedComponent.displayName;
  return AppContext;
}
